import React from "react";
import CodeBlock from "../components/CodeBlock";

const McpIntegration = () => (
  <section id="mcp-integration" className="developer-section">
    <h2>Agents and MCP Services</h2>
    <p>
      The <strong>Model Context Protocol (MCP)</strong> is an open standard that lets AI assistants
      connect to external tools and data sources. Plex hosts an MCP server directly &mdash; no
      local server to install &mdash; exposing the knowledge graph through four tools, enabling
      any MCP-compatible AI (Claude, ChatGPT, Gemini) to search biomedical data, resolve entities,
      and run multi-step agentic analyses.
    </p>

    <h3>How It Works</h3>
    <p>
      The hosted server is a <strong>Streamable HTTP</strong> MCP endpoint at{" "}
      <code>https://plexsearch.com/mcp</code>, authenticated with your Plex API key. When the AI
      decides it needs biomedical data, it calls one of the Plex tools through MCP; the server
      executes it against the Plex REST API and returns the results to the AI for reasoning.
    </p>
    <pre className="dev-code-pre"><code>{`AI Assistant  <-->  MCP Protocol (Streamable HTTP)  <-->  https://plexsearch.com/mcp
                                                                |
                                                          Tool: resolve
                                                          Tool: guide_agent
                                                          Tool: search_analyst
                                                          Tool: task_result
                                                          Prompt: task_polling_instructions`}</code></pre>

    <h3>MCP Tool Definitions</h3>

    <div className="dev-mcp-tool">
      <h4><code>resolve</code> &mdash; Resolve Entity Names to IDs</h4>
      <p>
        Convert free-text entity names (drug names, gene symbols, disease names) into
        canonical Plex entity IDs with confidence scores.
      </p>
      <CodeBlock
        title="resolve tool schema"
        examples={{
          json: `{
  "name": "resolve",
  "description": "Resolve free-text entity names to canonical Plex entity IDs. Use this before searching when you have names instead of IDs.",
  "inputSchema": {
    "type": "object",
    "properties": {
      "terms": {
        "type": "array",
        "items": { "type": "string" },
        "description": "Entity names to resolve (e.g., ['aspirin', 'BRCA1', 'lung cancer'])"
      }
    },
    "required": ["terms"]
  }
}`,
        }}
      />
    </div>

    <div className="dev-mcp-tool">
      <h4><code>guide_agent</code> &mdash; Run a Guided Research Task</h4>
      <p>
        Hands a research question to Plex's own agent, which plans and runs the search/evidence
        steps needed to answer it. Long-running: starts a task you poll with{" "}
        <code>task_result</code>.
      </p>
    </div>

    <div className="dev-mcp-tool">
      <h4><code>search_analyst</code> &mdash; Run a Guided Search + Analysis Task</h4>
      <p>
        Runs a search grounded in your query entities and has an LLM analyze the results.
        Also long-running: starts a task you poll with <code>task_result</code>.
      </p>
    </div>

    <div className="dev-mcp-tool">
      <h4><code>task_result</code> &mdash; Poll a Background Task</h4>
      <p>
        Fetches the status/result of a task started by <code>guide_agent</code> or{" "}
        <code>search_analyst</code>. Follow the <code>task_polling_instructions</code> prompt
        for the recommended polling cadence and backoff.
      </p>
    </div>

    <h3>Connecting to the Hosted Server</h3>
    <p>
      No server to run &mdash; point your MCP client at the hosted endpoint with your API key
      in the <code>x-api-key</code> header:
    </p>
    <CodeBlock
      title="Claude Code"
      examples={{
        curl: `claude mcp add --transport http plex https://plexsearch.com/mcp \\
  --header "x-api-key: <your-api-key>"`,
      }}
    />
    <CodeBlock
      title="claude_desktop_config.json (or any client supporting remote MCP servers)"
      examples={{
        json: `{
  "mcpServers": {
    "plex": {
      "url": "https://plexsearch.com/mcp",
      "headers": {
        "x-api-key": "<your-api-key>"
      }
    }
  }
}`,
      }}
    />

    <h3>Authentication</h3>
    <p>
      Generate a long-lived API key from your Plex account's Profile &rarr; API Key page, and
      pass it as <code>x-api-key: &#123;api_key&#125;</code> on the MCP connection.
    </p>
  </section>
);

export default McpIntegration;
